import React from 'react';
import { Check, Phone, MessageCircle, Award, Shield, Clock, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, vistaFashionProducts } from '../mockData';
import { Link } from 'react-router-dom';

const VistaFashion = () => {
  const handleWhatsAppClick = () => {
    const phone = businessInfo.phones[0].replace(/\s+/g, '').replace('+', '');
    const message = encodeURIComponent('Hi, I am interested in Vista Fashion blinds.');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const benefits = [
    {
      icon: Award,
      title: 'Premium Quality',
      description: 'Vista Fashion is known for superior materials and craftsmanship'
    },
    {
      icon: Shield,
      title: '5 Year Warranty',
      description: 'Comprehensive warranty coverage on all Vista Fashion products'
    },
    {
      icon: Clock,
      title: 'Durable',
      description: 'Built to last with fade-resistant fabrics and robust mechanisms'
    },
    {
      icon: Settings,
      title: 'Customizable',
      description: 'Wide range of colors, sizes, and motorization options available'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 via-white to-orange-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              Authorized Dealer
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Vista Fashion{' '}
              <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Window Blinds
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Premium quality window blinds from India's leading brand. As authorized Vista Fashion dealers, we bring you the finest selection of blinds for your home and office.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => window.location.href = '/contact'}
              >
                Contact Us
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-amber-600 text-amber-700 hover:bg-amber-50"
                onClick={handleWhatsAppClick}
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Vista Fashion */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Vista Fashion Blinds?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              India&apos;s most trusted brand for window blinds
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all text-center">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Vista Fashion Product Range
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our comprehensive range of premium window blinds
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {vistaFashionProducts.map((product) => (
              <Card key={product.id} className="group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all">
                <div className="relative overflow-hidden aspect-square">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{product.name}</h3>
                  <ul className="space-y-2 mb-5">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    size="sm"
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Get Quote
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Vista Fashion Blinds Features
              </h2>
              <div className="space-y-4">
                {[
                  'UV Protection - Protects your furniture from sun damage',
                  'Energy Efficient - Helps maintain room temperature',
                  'Easy to Clean - Low maintenance and long-lasting',
                  'Child Safe - Safety mechanisms for homes with children',
                  'Noise Reduction - Reduces external noise pollution',
                  'Privacy Control - Complete privacy when needed',
                  'Light Control - Precise control over natural light',
                  'Motorization Available - Smart home compatible options'
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Check className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-lg text-gray-700">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1616486029423-aaa4789e8c9a?w=800&q=80"
                alt="Vista Fashion Blinds"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Installation Process
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Site Visit', description: 'Free measurement and consultation at your location' },
              { step: '2', title: 'Selection', description: 'Choose from wide range of colors and styles' },
              { step: '3', title: 'Installation', description: 'Professional installation by trained experts' },
              { step: '4', title: 'Support', description: 'After-sales support and warranty service' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-amber-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Upgrade Your Windows?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get expert advice on Vista Fashion blinds
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="bg-white text-amber-700 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </Link>
            <a href={`tel:${businessInfo.phones[0]}`}>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-amber-700 px-8 py-6 text-lg">
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VistaFashion;
